import { Data } from "../interface/Data"
import { Values } from "../interface/Values"
import { DOMAINS } from "./domains"

// ---------- HEATING ---------- //

/**
 * Calculates heating according solely to window.
 * Chauffage = - 4 * window
 * @param window Value for window parameter.
 * @returns Value for heating.
 */
export function computeHeatingByWindow(window: number) {
    if (window < 2)
        return 5
    else if (window > 3)
        return 0
    else
        return -5
}

/**
 * Calculates heating according to temperature.
 * Chauffage = 3 * temp
 * @param temp Value for temperature parameter.
 * @returns Value for heating.
 */
export function computeHeatingByTemp(temp: number) {
    return temp > 10 ? 5 * temp : 2 * temp
}

/**
 * Calculates heating according to curtains.
 * Chauffage = - 2 * curtains
 * @param curtains Value for curtains parameter.
 * @returns Value for heating.
 */
 export function computeHeatingByCurtains(curtains: number) {
    return - 2 * curtains
}

/**
 * Calculates heating according to window, temperature and curtains.
 * Chauffage = - 4 * window + 3 * temp - 2 * curtains
 * @param window Value for window parameter.
 * @param temp Value for temperature parameter.
 * @param curtains Value for curtains parameter.
 * @returns Value for heating.
 */
export function computeHeating(window: number, temp: number, curtains: number) {
    return computeHeatingByWindow(window) + computeHeatingByTemp(temp) + computeHeatingByCurtains(curtains)
}

// ---------- AIR CONDITIONING ---------- //

/**
 * Calculates air conditioning according to windows.
 * Climatisation = - 2 * window
 * @param window Value for window parameter.
 * @returns Value for air conditioning.
 */
export function computeAirConditioningByWindow(window: number) {
    return - 2 * window
}

/**
 * Calculates air conditioning according to temperature.
 * Climatisation = temp
 * @param temp Value for temperature parameter.
 * @returns Value for air conditioning.
 */
export function computeAirConditioningByTemp(temp: number) {
    return temp
}

/**
 * Calculates air conditioning according to temperature.
 * Climatisation = - 3 * curtains
 * @param curtains Value for curtains parameter.
 * @returns Value for air conditioning.
 */
 export function computeAirConditioningByCurtains(curtains: number) {
    return - 3 * curtains
}

/**
 * Calculates air conditioning according to window, temperature and curtains.
 * Climatisation = temp - 2 * window - 3 * curtains
 * @param window Value for window parameter.
 * @param temp Value for temperature parameter.
 * @param curtains Value for curtains parameter.
 * @returns Value for air conditioning.
 */
export function computeAirConditioning(window: number, temp: number, curtains: number) {
    return computeAirConditioningByWindow(window) + computeAirConditioningByTemp(temp) + computeAirConditioningByCurtains(curtains)
}

// ---------- ELECTRICITY ---------- //

/**
 * Calculates electricity according to electric equipment.
 * Électricité = electric_equipement
 * @param electric_equipement Value for electric equipement parameter.
 * @returns Value for electricity.
 */
 export function computeElectricityByElectricEquipment(electric_equipement: number) {
    return electric_equipement
}


/**
 * Calculates electricity according to portatif ventilation.
 * Électricité = 2 * portatif_ventilation
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @returns Value for electricity.
 */
 export function computeElectricityByPortVent() {
    return 2 * 0
}


/**
 * Calculates electricity according to artificial light.
 * Électricité = - 4 * artificial_light
 * @param artificial_light Value for artificial light parameter.
 * @returns Value for electricity.
 */
 export function computeElectricityByArtificialLight(artificial_light: number) {
    return - 4 * artificial_light
}

/**
 * Calculates electricity according to electric mecanical ventilation.
 * Électricité = 3 * mecanical_ventilation
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @returns Value for electricity.
 */
export function computeElectricityByMecVent(mecanical_ventilation: number) {
    return 3 * mecanical_ventilation
}


/**
 * Calculates electricity according to electric equipment, portatif ventilation, artificial light and mecanical ventilation.
 * Électricité = electric_equipement + 2 * portatif_ventilation - 4 * artificial_light + 3 * mecanical_ventilation
 * @param electric_equipement Value for electric equipement parameter.
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @param artificial_light Value for artificial light parameter.
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @returns Value for electricity.
 */
 export function computeElectricity(electric_equipement: number, artificial_light: number, mecanical_ventilation: number) {
    return computeElectricityByElectricEquipment(electric_equipement)
        + computeElectricityByPortVent()
        + computeElectricityByArtificialLight(artificial_light)
        + computeElectricityByMecVent(mecanical_ventilation)
}

// ---------- AIR QUALITY ---------- //

/**
 * Calculates air quality according to mecanical ventilation.
 * Qualité de l'air = mecanical_ventilation * 3
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @returns Value for air quality.
 */
 export function computeAirQualityByMecVent(mecanical_ventilation: number) {
    return mecanical_ventilation * 3
}


/**
 * Calculates air quality according to windows.
 * Qualité de l'air = window
 * @param window Value for window parameter.
 * @returns Value for air quality.
 */
 export function computeAirQualityByWindow(window: number) {
    return window
}

/**
 * Calculates air quality according to mecanical ventilation and windows.
 * Qualité de l'air = mecanical_ventilation * 3 + window
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @param window Value for window parameter.
 * @returns Value for air quality.
 */
export function computeAirQuality(mecanical_ventilation: number, window: number) {
    return computeAirQualityByMecVent(mecanical_ventilation) + computeAirConditioningByWindow(window)
}

// ---------- SOUND COMFORT ---------- //

/**
 * Calculates sound comfort according to windows.
 * Qualité du son = window * -2
 * @param window Value for window parameter.
 * @returns Value for sound comfort.
 */
 export function computeSoundComfortByWindow(window: number) {
    return window * -2
}

/**
 * Calculates sound comfort according to portatif ventilation.
 * Qualité du son = portatif_ventilation
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @returns Value for sound comfort.
 */
 export function computeSoundComfortByPortVent() {
    return 0
}

/**
 * Calculates sound comfort according to mecanical ventilation.
 * Qualité du son = - 3 * mecanical_ventilation
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @returns Value for sound comfort.
 */
 export function computeSoundComfortByMecVent(mecanical_ventilation: number) {
    return - 3 * mecanical_ventilation
}

/**
 * Calculates sound comfort according to windows, portatif ventilation and mecanical ventilation.
 * Qualité du son = window * -2 + portatif_ventilation - 3 * mecanical_ventilation
 * @param window Value for window parameter.
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @param mecanical_ventilation Value for mecanical ventilation parameter.
 * @returns Value for sound comfort.
 */
export function computeSoundComfort(window: number, mecanical_ventilation: number) {
    return computeSoundComfortByWindow(window) + computeSoundComfortByPortVent()
        + computeSoundComfortByMecVent(mecanical_ventilation)
}

// ---------- VISUAL COMFORT ---------- //

/**
 * Calculates visual comfort according to artificial light.
 * Éclairage = artificial_light
 * @param artificial_light Value for window parameter.
 * @returns Value for visual comfort.
 */
 export function computeVisualComfortByArtificialLight(artificial_light: number) {
    return artificial_light * 2
}

/**
 * Calculates visual comfort according to curtains.
 * Éclairage = curtains
 * @param curtains Value for curtains parameter.
 * @returns Value for visual comfort.
 */
 export function computeVisualComfortByCurtains(curtains: number) {
    return curtains
}

/**
 * Calculates visual comfort according to artificial light and curtains.
 * Éclairage = artificial_light * 2 + curtains
 * @param artificial_light Value for window parameter.
 * @param curtains Value for curtains parameter.
 * @returns Value for visual comfort.
 */
export function computeVisualComfort(artificial_light: number, curtains: number) {
    return computeVisualComfortByArtificialLight(artificial_light) + computeVisualComfortByCurtains(curtains)
}

// ---------- THERMIC COMFORT ---------- //

/**
 * Calculates thermic comfort according to windows.
 * Confort thermique = -6 * window
 * @param window Value for window parameter.
 * @returns Value for thermic comfort.
 */
 export function computeThermicComfortByWindow(window: number) {
    return -6 * window
}

/**
 * Calculates thermic comfort according to curtains.
 * Confort thermique = 4 * curtains
 * @param curtains Value for curtains parameter.
 * @returns Value for thermic comfort.
 */
 export function computeThermicComfortByCurtains(curtains: number) {
    return 4 * curtains
}

/**
 * Calculates thermic comfort according to temperature.
 * Confort thermique = - 5 * temp
 * @param temp Value for temperature parameter.
 * @returns Value for thermic comfort.
 */
 export function computeThermicComfortByTemp(temp: number) {
    return - 5 * temp
}

/**
 * Calculates thermic comfort according to portatif ventilation.
 * Confort thermique = portatif_ventilation
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @returns Value for thermic comfort.
 */
 export function computeThermicComfortByPortVent() {
    return 0
}

/**
 * Calculates thermic comfort according to clothings.
 * Confort thermique = 2 * dressing
 * @param dressing Value for dressing parameter.
 * @returns Value for thermic comfort.
 */
 export function computeThermicComfortByClothing(dressing: number) {
    return 2 * dressing
}

/**
 * Calculates thermic comfort according to windows, curtains, temperature, portatif ventilation and clothings.
 * Confort thermique = -6 * window + 4 * curtains - 5 * temp + portatif_ventilation + 2 * dressing
 * @param window Value for window parameter.
 * @param curtains Value for curtains parameter.
 * @param temp Value for temperature parameter.
 * @param portatif_ventilation Value for portatif ventilation parameter.
 * @param dressing Value for dressing parameter.
 * @returns Value for thermic comfort.
 */
export function computeThermicComfort(window: number, curtains: number, temp: number, dressing: number) {
    return computeThermicComfortByWindow(window)
        + computeThermicComfortByCurtains(curtains)
        + computeThermicComfortByTemp(temp)
        + computeThermicComfortByClothing(dressing)
}

// ---------- COSTS ---------- //

/**
 * Calculates cost according to heating, air conditioning and electricity.
 * Coûts = chauffage + climatisation * 2 + 3 * electricity
 * @param chauffage Value for heating parameter.
 * @param climatisation Value for air conditioning parameter.
 * @param electricity Value for electricity parameter.
 * @returns Value for cost.
 */
export function computeCost(chauffage: number, climatisation: number, electricity: number) {
    return chauffage + climatisation * 2 + 3 * electricity
}

// ---------- GES ---------- //

/**
 * Calculates GES according to heating, air conditioning and electricity.
 * GES = 3 * chauffage + climatisation * .5 + .33 * electricity
 * @param chauffage Value for heating parameter.
 * @param climatisation Value for air conditioning parameter.
 * @param electricity Value for electricity parameter.
 * @returns Value for GES.
 */
export function computeGES(chauffage: number, climatisation: number, electricity: number) {
    return 3 * chauffage + climatisation * .5 + .33 * electricity
}

export function computeValues(data: Data) {
    const chauffage = computeHeating(data.window, data.temp, data.curtains);
    const climatisation = computeAirConditioning(data.window, data.temp, data.curtains);
    const electricity = computeElectricity(data.electric_equipement, data.artificial_light, data.mecanical_ventilation);
    const airQuality = computeAirQuality(data.mecanical_ventilation, data.window);
    const soundConfort = computeSoundComfort(data.window, data.mecanical_ventilation);
    const visualConfort = computeVisualComfort(data.artificial_light, data.curtains);
    const thermalConfort = computeThermicComfort(data.window, data.curtains, data.temp, data.dressing);

    return {
        chauffage: chauffage,
        climatisation: climatisation,
        electricity: electricity,
        air_quality: airQuality,
        visual_comfort: visualConfort,
        thermal_comfort: thermalConfort,
        cost: 3 * chauffage + climatisation + electricity,
        ges: 2 * chauffage + 2 * climatisation + electricity,
        energie_tot: 0,
        app_elect: electricity
    }
}

export function computeScore(values: Values) {
    return Math.round(values.air_quality + values.thermal_comfort + values.visual_comfort - (values.cost + values.ges));
}


export function getMaxHeating() { return computeHeating(DOMAINS.window[0], DOMAINS.temp[1], DOMAINS.window[0]) }
export function getMinHeating() { return computeHeating(DOMAINS.window[1], DOMAINS.temp[0], DOMAINS.window[1]) }
export function getMaxAirConditioning() { return computeAirConditioning(DOMAINS.window[0], DOMAINS.temp[1], DOMAINS.curtains[0]) }
export function getMinAirConditioning() { return computeAirConditioning(DOMAINS.window[1], DOMAINS.temp[0], DOMAINS.curtains[1]) }
export function getMaxElectricity() { return computeElectricity(DOMAINS.electric_equipement[1], DOMAINS.artificial_light[0], DOMAINS.mecanical_ventilation[1]) }
export function getMinElectricity() { return computeElectricity(DOMAINS.electric_equipement[0], DOMAINS.artificial_light[1], DOMAINS.mecanical_ventilation[0]) }
export function getMaxAirQuality() { return computeAirQuality(DOMAINS.mecanical_ventilation[1], DOMAINS.window[1]) }
export function getMinAirQuality() { return computeAirQuality(DOMAINS.mecanical_ventilation[0], DOMAINS.window[0]) }
export function getMaxSoundComfort() { return computeSoundComfort(DOMAINS.window[0], DOMAINS.mecanical_ventilation[0]) }
export function getMinSoundComfort() { return computeSoundComfort(DOMAINS.window[1], DOMAINS.mecanical_ventilation[1]) }
export function getMaxVisualComfort() { return computeVisualComfort(DOMAINS.artificial_light[1], DOMAINS.curtains[1]) }
export function getMinVisualComfort() { return computeVisualComfort(DOMAINS.artificial_light[0], DOMAINS.curtains[0]) }
export function getMaxThermalComfort() { return computeThermicComfort(DOMAINS.window[0], DOMAINS.curtains[1], DOMAINS.temp[0], DOMAINS.dressing[1]) }
export function getMinThermalComfort() { return computeThermicComfort(DOMAINS.window[1], DOMAINS.curtains[0], DOMAINS.temp[1], DOMAINS.dressing[0]) }

export function getMinCosts() { return computeCost(getMinHeating(), getMinAirConditioning(), getMinElectricity()); }
export function getMaxCosts() { return computeCost(getMaxHeating(), getMaxAirConditioning(), getMaxElectricity()); }

export function getMinGES() { return computeGES(getMinHeating(), getMinAirConditioning(), getMinElectricity()); }
export function getMaxGES() { return computeGES(getMaxHeating(), getMaxAirConditioning(), getMaxElectricity()); }
